/*
 * File: PatientStore.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: MST Store for patient
 */
import {
  types,
  flow,
  getSnapshot,
  Instance,
  cast,
  SnapshotOut
} from "mobx-state-tree";
import Api from "./Api";
import PatientModel from "../demographics/patient_info/store/DemographicsModel";
import RecentPatientActions from "services/patients/store/RecentPatientActions";
import PatientIdStore from "services/patients/store/PatientIdStore";
import { CustomInitStore } from "utils/useStore";
import Preferences from "services/preferences/Store";

const PatientStore = types
  .model("PatientStore", {
    patientId: types.maybe(types.number),
    patientModel: types.maybe(PatientModel),
    editPatient: types.maybe(PatientModel),
    newPatient: types.maybe(PatientModel)
  })
  .volatile(() => ({
    loading: false
  }))
  .actions(self => ({
    resetAfterPatientCreation() {
      self.patientId = undefined;
      self.patientModel = undefined;
      self.editPatient = undefined;
      self.newPatient = undefined;
      self.loading = false;
    },
    setPatientId(patientId?: number) {
      if (!patientId) {
        patientId = PatientIdStore.getId();
      } else {
        PatientIdStore.setId(patientId);
      }
      self.patientId = patientId;
    },
    afterCreate() {
      self.patientModel = PatientModel.create();
      self.editPatient = PatientModel.create();
      self.newPatient = PatientModel.create();
    },

    find: flow(function*(preferencesStore: Instance<typeof Preferences>) {
      try {
        const patientId = self.patientId;
        self.loading = true;
        if (patientId) {
          const patient: Instance<typeof PatientModel> | null = yield Api.find(
            patientId
          );
          self.patientModel = patient ?? cast({});
          self.editPatient = patient ?? cast({});
          self.newPatient?.updateNewPatient(self.patientModel);

          if (patient !== null) {
            yield RecentPatientActions.selectPatient(patient, preferencesStore);
          }
        }
        self.loading = false;
      } catch (error) {
        console.error("Error fetching patient", error);
      }
    })
  }))
  .actions(self => ({
    patientCard: flow(function*(
      recentPatientStore: Instance<typeof Preferences>
    ): Generator<any, SnapshotOut<typeof PatientModel> | undefined> {
      if (!self.patientModel?.patientId) {
        yield self.find(recentPatientStore);
      }
      if (self.patientModel !== undefined) {
        return getSnapshot(self.patientModel);
      } else {
        return undefined;
      }
    })
  }));

(PatientStore as CustomInitStore<
  Instance<typeof PatientStore>
>)._onGetStoreFromContext = (store, env) => {
  const create = (patientId?: number) => {
    if (!patientId) {
      patientId = PatientIdStore.getId();
    } else {
      PatientIdStore.setId(patientId);
    }
    const instance = PatientStore.create({ patientId: patientId }, env);
    return instance;
  };

  if (!store) {
    return create();
  } else if (store.patientId !== PatientIdStore.getId()) {
    /*
    If the existing patient store id doesn't match the patientId in storage, we need to create a new store.
    This happens when we update the patient id in storage and try to access the patient store without updating it.

    This store seems like it really should just be resetting the existing store to it's default state to ensure MobX can update all the views that were using info about the selected patient, however other code seems to be relying on that not happening when a new patient is selected (presumably because the old factory was doing it that way for whatever reason).
    */
    return create();
  }

  return store;
};

export default PatientStore;
